import { useEffect, useState } from 'react';
import {
  DEFAULT_LEARNING_RESTRICTIONS,
  getLearningRestrictions,
  saveLearningRestrictions,
  type LearningRestrictionSettings,
} from '@/utils/learningRestrictions';

const restrictionItems: {
  key: keyof LearningRestrictionSettings;
  label: string;
  description: string;
}[] = [
  {
    key: 'quizEnabled',
    label: 'クイズ',
    description: 'クイズメニュー・履歴・回答画面',
  },
  {
    key: 'mockExamEnabled',
    label: '模擬試験',
    description: '模擬試験メニュー・履歴・回答画面',
  },
  {
    key: 'viewTextEnabled',
    label: 'テキスト閲覧',
    description: 'テキスト閲覧ページ',
  },
  {
    key: 'examEnabled',
    label: '試験を受ける',
    description: '試験選択・受験ページ',
  },
];

export default function LearningRestrictionsPage() {
  const [settings, setSettings] = useState<LearningRestrictionSettings>(
    DEFAULT_LEARNING_RESTRICTIONS
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getLearningRestrictions();
        setSettings(data);
      } catch (error) {
        console.error('Error loading learning restrictions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleToggle = (key: keyof LearningRestrictionSettings) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setStatusMessage(null);

    try {
      await saveLearningRestrictions(settings);
      setStatusMessage('保存しました。');
    } catch (error) {
      console.error('Error saving learning restrictions:', error);
      setStatusMessage('保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">学習・試験メニュー制限</h2>
        <p className="mt-2 text-sm text-gray-600">
          システム不具合時にユーザーのアクセスを制限できます。OFFにしたメニューは利用時にポップアップで案内されます。
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
        {loading ? (
          <p className="text-sm text-gray-500">設定を読み込み中...</p>
        ) : (
          <div className="space-y-4">
            {restrictionItems.map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between gap-4 rounded-xl border border-gray-100 px-4 py-3"
              >
                <div>
                  <p className="text-sm font-semibold text-gray-800">{item.label}</p>
                  <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle(item.key)}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                    settings[item.key] ? 'bg-emerald-500' : 'bg-gray-300'
                  }`}
                  aria-pressed={settings[item.key]}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                      settings[item.key] ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-800">ポップアップメッセージ</label>
          <textarea
            value={settings.message}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                message: event.target.value,
              }))
            }
            rows={4}
            className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-700 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            placeholder="表示するメッセージを入力してください"
          />
          <p className="mt-2 text-xs text-gray-500">
            ここで設定したメッセージが、制限中のメニューを選択した際に表示されます。
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center justify-center rounded-xl bg-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? '保存中...' : '設定を保存'}
          </button>
          {statusMessage && <span className="text-sm text-gray-600">{statusMessage}</span>}
        </div>
      </div>
    </div>
  );
}
